import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { fabric } from 'fabric';
import moment from 'moment';
import { Subscription } from 'rxjs';

import {
  CANVAS_DARK_THEME,
  CANVAS_LIGHT_THEME,
  CanvasColors,
} from '@tablesready/canvas';
import { FrontendScrollbarComponent } from '@tablesready/frontend-scrollbar';
import { FloorPlanParty, FloorPlanPartyType } from './frontend-timeline.intefaces';
import { AppTheme, ThemeSwitchService } from '@tablesready/frontend-theme-switch';
import { ClockService } from '@tablesready/frontend-alerts-bar';

const HOURS = 24;
const MINUTE = 60;
const HALF = 2;

@Component({
  selector: 'tr-timeline',
  templateUrl: './frontend-timeline.component.html',
  styleUrls: ['./frontend-timeline.component.scss'],
})
export class FrontendTimelineComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChild(FrontendScrollbarComponent) scrollbarRef?: FrontendScrollbarComponent;
  @ViewChild('canvasAvailableArea') canvasAvailableAreaRef?: ElementRef;
  @Input() targetCanvasWidth = 1224;
  @Input() targetCanvasHeight = 60;
  @Input() tablesHeight = 30;
  @Input() canvasAreaWidth = '100%';
  @Input() canvasAreaHeight = '100%';
  @Input() anHourWidth = 10;
  @Input() hoursToShow = 48;
  @Input() partiesToShow: FloorPlanParty[] = [];
  @Input() horizontalOffset = 24;
  @Input() tableColors?: Record<FloorPlanPartyType, string>;
  @Input() showCurrentTime = true;
  @Input() resourceId = '';
  @Input() timezone = 'America/New_York';

  actualCanvasWidth = this.targetCanvasWidth;
  actualCanvasHeight = this.targetCanvasHeight;
  scrollableAreaWidth = this.targetCanvasWidth;
  scrollableAreaHeight = this.targetCanvasHeight;

  protected canvas?: fabric.Canvas;
  private currentTimeGroup?: fabric.Group;
  private themeColors: CanvasColors;
  private readonly subscription = new Subscription();

  constructor(
    private readonly clockService: ClockService,
    private readonly themeSwitch: ThemeSwitchService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.themeColors = this.themeToColors(AppTheme.dark);
  }

  ngOnInit(): void {
    this.setThemeColors(this.themeSwitch.getSavedTheme());
    const themeSub = this.themeSwitch.themeChange$
      .subscribe((theme) => {
        this.setThemeColors(theme);
        this.draw();
      });
    this.subscription.add(themeSub);
  }

  setThemeColors(theme: AppTheme): void {
    this.themeColors = this.themeToColors(theme);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['partiesToShow']) {
      this.deleteAllTables();
      this.drawParties();
      this.panToCurrentTime();
    }

    if (changes['showCurrentTime']) {
      this.setCurrentTimeColor();
    }
  }

  setCanvasDimensions(): void {
    if (!this.canvas || !this.canvasAvailableAreaRef) {
      return;
    }

    const { offsetWidth, offsetHeight } = this.canvasAvailableAreaRef.nativeElement as HTMLElement;
    const OFFSET_FOR_SCROLLBAR_PX = 14;
    this.actualCanvasWidth = offsetWidth;
    this.actualCanvasHeight = offsetHeight - OFFSET_FOR_SCROLLBAR_PX;

    this.canvas.setWidth(this.actualCanvasWidth);
    this.canvas.setHeight(this.actualCanvasHeight);
  }

  ngAfterViewInit(): void {
    this.draw();

    const clockSub = this.clockService.clock$
      .subscribe((res) => {
        this.updateCurrentTime(res);
      });
    this.subscription.add(clockSub);

    if (!this.scrollbarRef) {
      return;
    }

    const scrollSub = this.scrollbarRef.scrolled$.subscribe(() => {
      if (!this.scrollbarRef) {
        return;
      }

      const { scrollTop, scrollLeft } = this.scrollbarRef;
      this.canvas?.absolutePan({
        x: scrollLeft,
        y: scrollTop,
      });
    });
    this.subscription.add(scrollSub);
  }

  updateCurrentTime(currentTime: number): void {
    if (!this.currentTimeGroup) {
      return;
    }

    const time = moment.tz(currentTime, this.timezone);
    const timeOffset = ((time.hour() + (time.minute() / MINUTE)) * this.anHourWidth * HALF) + this.horizontalOffset;
    this.currentTimeGroup.set('left', timeOffset);
    this.currentTimeGroup.bringToFront();
  }

  panToCurrentTime(): void {
    if (!this.showCurrentTime) {
      this.canvas?.absolutePan({
        x: 0,
        y: 0,
      });
      this.setScrollSize();
      return;
    }

    if (!this.currentTimeGroup || !this.canvas) {
      return;
    }

    const { tl, tr } = this.canvas.calcViewportBoundaries();
    const viewportWidth = tr.x - tl.x;
    const viewportCenter = (this.currentTimeGroup.left ?? 0) - (viewportWidth / HALF);

    this.canvas.absolutePan({
      x: Math.max(Math.min(viewportCenter, this.targetCanvasWidth), 0),
      y: 0,
    });
    this.setScrollSize();
  }

  deleteAllTables(): void {
    if (!this.canvas) {
      return;
    }

    this.canvas.getObjects('rect').forEach((table) => {
      this.canvas?.remove(table);
    });
    this.canvas.renderAll();
  }

  drawParties(): void {
    this.partiesToShow.forEach((party) => {
      if (!this.canvas || !party.resources.includes(this.resourceId)) {
        return;
      }

      const SEC_TO_MILLI = 1000;
      const quotedTime = moment.tz(party.quoted_time, this.timezone);
      const shift = quotedTime.hour() + (quotedTime.minute() / MINUTE);

      const rect = new fabric.Rect({
        left: ((this.anHourWidth * 2) * shift) + this.horizontalOffset,
        top: 0,
        originX: 'left',
        originY: 'top',
        stroke: this.themeColors?.shapeStroke,
        width: (party.duration / SEC_TO_MILLI / MINUTE / MINUTE) * (this.anHourWidth * 2),
        height: this.tablesHeight,
        strokeWidth: 0,
        fill: this.tableColors ? this.tableColors[party.cardType] : this.themeColors?.shapeFill,
        selectable: false,
        lockMovementX: true,
        lockMovementY: true,
        hoverCursor: 'default',
        moveCursor: 'default',
      });
      this.canvas.add(rect);
    });
  }

  private drawGrid(): void {
    if (!this.canvas) {
      return;
    }

    this.anHourWidth = (this.targetCanvasWidth - (this.horizontalOffset * HALF)) / this.hoursToShow;

    for (let i = 0; i < this.hoursToShow + 1; i++) {
      const posX = (i * this.anHourWidth) + this.horizontalOffset;

      this.canvas.add(new fabric.Line(
        [posX, 0, posX, this.tablesHeight],
        {
          type: 'line',
          strokeWidth: 1,
          stroke: this.themeColors?.dividerColor,
          hasBorders: false,
          lockMovementX: true,
          lockMovementY: true,
          hasControls: false,
          selectable: false,
          evented: false,
          objectCaching: false,
        },
      ));

      if (i % HALF === 0) {
        let hourText = String(i / HALF);

        if (i === 0) {
          hourText = '12AM';
        } else if (i < HOURS) {
          hourText += 'AM';
        }

        if (i === HOURS) {
          hourText = '12PM';
        } else if (i === HOURS * HALF) {
          hourText = '12AM';
        } else if (i >= HOURS) {
          hourText = `${(i - HOURS) / HALF}PM`;
        }

        this.canvas.add(new fabric.Text(String(hourText), {
          left: posX,
          top: this.tablesHeight,
          fill: this.themeColors?.primaryColor,
          fontSize: 13,
          fontFamily: 'Source Sans Pro',
          fontWeight: 400,
          opacity: .6,
          fontStyle: 'normal',
          selectable: false,
          lockMovementX: true,
          lockMovementY: true,
          originX: 'center',
          hoverCursor: 'default',
          moveCursor: 'default',
        }));
      }
    }
  }

  private draw(): void {
    setTimeout(() => {
      this.canvas = new fabric.Canvas('timelineSurface', {
        allowTouchScrolling: true,
        backgroundColor: this.themeColors?.backgroundColor,
        selection: false,
        preserveObjectStacking: true,
      });

      this.drawGrid();
      this.setCanvasDimensions();
      this.setScrollSize();
      this.drawParties();

      this.currentTimeGroup = new fabric.Group([
        new fabric.Line([0, 0, 0, this.targetCanvasHeight]),
      ], {
        left: 0,
        top: 0,
        height: this.targetCanvasHeight,
        selectable: false,
        lockMovementX: true,
        lockMovementY: true,
        hoverCursor: 'default',
        moveCursor: 'default',
      });
      this.canvas.add(this.currentTimeGroup);
      this.setCurrentTimeColor();

      this.updateCurrentTime(Date.now());
      setTimeout(() => {
        this.panToCurrentTime();
      });
    });
  }

  private setCurrentTimeColor(): void {
    if (!this.currentTimeGroup) {
      return;
    }

    this.currentTimeGroup.getObjects('line').forEach((line) => {
      line.set('stroke', this.showCurrentTime ? 'red' : 'transparent');
    });
  }

  private setScrollSize(): void {
    if (!this.canvas) {
      return;
    }

    const { x, y } = this.canvas.calcViewportBoundaries().tl;
    this.scrollableAreaWidth = this.targetCanvasWidth;
    this.scrollableAreaHeight = this.targetCanvasHeight;
    this.cdr.detectChanges();

    setTimeout(() => {
      if (this.scrollbarRef instanceof FrontendScrollbarComponent) {
        this.scrollbarRef.update(x, y);
      }
    });
  }

  private readonly themeToColors = (theme: AppTheme): CanvasColors => (theme === AppTheme.light
    ? CANVAS_LIGHT_THEME : CANVAS_DARK_THEME);
}
